import { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, ExternalLink } from "lucide-react";
import { useAuth, isStaff } from "@/context/AuthContext";
import { usePageTitle } from "@/lib/api";
import { divisionName } from "@/lib/content";
import { Loading } from "@/components/site/Bits";
import { ContentManager } from "@/components/admin/ContentManager";
import { SettingsPanel, InquiriesPanel, UsersPanel } from "@/components/admin/Panels";
import { ProfileRequestsPanel } from "@/components/admin/ProfileRequests";
import { BoardPhotoManager } from "@/components/admin/BoardPhotoManager";

const ADMIN_TABS = [
  ["settings", "Homepage & Settings"], ["board_photos", "Board Photo Manager"], ["board", "Board Members"], ["announcements", "Announcements"], ["athletes", "Athletes"], ["profile_requests", "Profile Requests"], ["invitations", "Invitations"],
  ["teams", "Teams & Rosters"], ["events", "Events"], ["articles", "Media Center"], ["sponsors", "Sponsors"],
  ["portal_updates", "Parent Portal Updates"], ["inquiries", "Inquiries"], ["users", "Users & Permissions"],
];
const COACH_TABS = [["teams", "My Team"], ["athletes", "My Roster"], ["portal_updates", "Parent Updates"]];

export default function Admin() {
  usePageTitle("Dashboard");
  const { user, logout } = useAuth();
  const tabs = user?.role === "admin" ? ADMIN_TABS : COACH_TABS;
  const [tab, setTab] = useState(null);
  if (user === null) return <div className="wrap pt-40"><Loading /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff(user)) return <Navigate to="/portal" replace />;
  if (user.role === "coach" && !user.team_slug) {
    return <div className="wrap pt-40 pb-20" data-testid="coach-no-team"><p className="h-display text-4xl">No team assigned yet</p><p className="mt-3 text-slate-400">Ask an administrator to assign your division.</p></div>;
  }
  const active = tab || tabs[0][0];
  return (
    <section className="bg-pitch min-h-screen pt-32 pb-20" data-testid="admin-page">
      <div className="wrap">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-line pb-6 mb-8">
          <div>
            <p className="eyebrow">{user.role === "admin" ? "Administrator" : `Coach • ${divisionName(user.team_slug)}`}</p>
            <h1 className="h-display text-4xl sm:text-5xl mt-2">Tri County Dashboard</h1>
            <p className="text-sm text-slate-400 mt-1">Signed in as {user.name} ({user.email})</p>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="btn-line !py-2 !text-sm" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" /> View Site</Link>
            <button onClick={logout} className="btn-line !py-2 !text-sm" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Sign Out</button>
          </div>
        </div>
        <div className="grid gap-8 lg:grid-cols-[230px_1fr]">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
            {tabs.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                className={`shrink-0 text-left px-4 py-3 text-xs font-bold uppercase tracking-wider border-l-4 transition-colors ${active === k ? "border-gold bg-navy text-gold" : "border-transparent text-slate-300 hover:bg-ink"}`}>
                {l}
              </button>
            ))}
          </nav>
          <div className="min-w-0">
            {active === "settings" && <SettingsPanel />}
            {active === "inquiries" && <InquiriesPanel />}
            {active === "users" && <UsersPanel me={user} />}
            {active === "profile_requests" && <ProfileRequestsPanel />}
            {active === "board_photos" && <BoardPhotoManager />}
            {!["settings", "inquiries", "users", "profile_requests", "board_photos"].includes(active) && <ContentManager key={active} kind={active} user={user} />}
          </div>
        </div>
      </div>
    </section>
  );
}
